import { Controller } from "@hotwired/stimulus";
import * as Turbo from "@hotwired/turbo";

// Generic, reusable tree-table controller for hierarchical tables with
// expand/collapse for arbitrary levels.
//
// Supports two patterns:
//   1. Lazy group-based: level 0 uses toggleLazyGroup with Turbo Stream loading
//   2. Group-based (supply reports): all levels use toggleGroup (no lazy-fetch)
//
// Collapse/expand all: use collapseLevel / expandLevel actions with a
// data-tree-table-target-level-param to specify which level to collapse.
//
// State preservation: expanded group IDs are saved to sessionStorage so
// expand/collapse state survives Turbo frame refreshes (sort, filter changes).
//
export default class extends Controller {
  static values = { persistKey: { type: String, default: "" } };

  connect() {
    if (this.persistKeyValue) {
      this.#restoreExpandedState();
    }
  }

  disconnect() {
    // Save state before teardown so it survives turbo frame replacements
    this.#saveExpandedState();
  }

  // Backwards-compatible alias for older tree-table markup.
  async toggleCycle(event) {
    await this.toggleLazyGroup(event);
  }

  // Expand/collapse a top-level row by loading its detail <tbody> via Turbo Stream.
  // Params: detailId (ID of the target <tbody>), src (URL to fetch)
  async toggleLazyGroup(event) {
    const row = event.currentTarget;
    const detailId = event.params.detailId;
    const src = event.params.src;
    if (!detailId) return;

    const detailTbody = document.getElementById(detailId);
    if (!detailTbody) return;

    const expanded = row.hasAttribute("data-expanded");

    if (expanded) {
      this.#collapseCycleRow(row, detailTbody);
    } else {
      row.setAttribute("data-expanded", "");
      detailTbody.classList.remove("hidden");
      this.#rotateChevron(row, true);

      if (!detailTbody.dataset.loaded && src) {
        await this.#fetchTurboStreamDetail(detailTbody, src);
      }
    }

    this.#saveExpandedState();
  }

  // Expand/collapse sub-level groups (client-side only, no server request).
  // Params: groupId (identifier matching child rows' parent param)
  toggleGroup(event) {
    const row = event.currentTarget;
    const groupId = event.params.groupId;
    if (!groupId) return;

    const expanded = row.hasAttribute("data-expanded");

    if (expanded) {
      row.removeAttribute("data-expanded");
      this.#rotateChevron(row, false);
      this.#hideDescendants(groupId);
    } else {
      row.setAttribute("data-expanded", "");
      this.#rotateChevron(row, true);
      this.#showDirectChildren(groupId);
    }

    this.#saveExpandedState();
  }

  stopPropagation(event) {
    event.stopPropagation();
  }

  // Collapse all rows at a given level and below.
  // Params: targetLevel (the level to collapse — all rows at this level
  //         and deeper become hidden/collapsed)
  collapseLevel(event) {
    const targetLevel = parseInt(event.params.targetLevel, 10);

    if (targetLevel === 0) {
      // Collapse all top-level rows (cycle-based or group-based)
      this.element
        .querySelectorAll('[data-tree-table-level="0"][data-expanded]')
        .forEach((row) => {
          const detailId = row.dataset.treeTableDetailIdParam;
          const tbody = detailId ? document.getElementById(detailId) : null;
          if (tbody) {
            this.#collapseCycleRow(row, tbody);
          } else {
            row.removeAttribute("data-expanded");
            this.#rotateChevron(row, false);
            const groupId = row.dataset.treeTableGroupIdParam;
            if (groupId) this.#hideDescendants(groupId);
          }
        });
    } else {
      // Collapse all group rows at this level
      this.element
        .querySelectorAll(
          `[data-tree-table-level="${targetLevel}"][data-expanded]`,
        )
        .forEach((row) => {
          row.removeAttribute("data-expanded");
          this.#rotateChevron(row, false);
          const groupId = row.dataset.treeTableGroupIdParam;
          if (groupId) this.#hideDescendants(groupId);
        });
    }

    this.#saveExpandedState();
  }

  // Expand all rows at a given level (only works for sub-levels, not cycle lazy-load).
  // Params: targetLevel (the level to expand)
  expandLevel(event) {
    const targetLevel = parseInt(event.params.targetLevel, 10);

    this.element
      .querySelectorAll(
        `[data-tree-table-level="${targetLevel}"]:not([data-expanded]):not(.hidden)`,
      )
      .forEach((row) => {
        const groupId = row.dataset.treeTableGroupIdParam;
        if (groupId) {
          row.setAttribute("data-expanded", "");
          this.#rotateChevron(row, true);
          this.#showDirectChildren(groupId);
        }
      });

    this.#saveExpandedState();
  }

  // --- Private ---

  #collapseCycleRow(row, detailTbody) {
    row.removeAttribute("data-expanded");
    detailTbody.classList.add("hidden");
    this.#rotateChevron(row, false);
  }

  async #fetchTurboStreamDetail(tbody, url) {
    try {
      const response = await fetch(url, {
        headers: {
          Accept: "text/vnd.turbo-stream.html",
          "X-Requested-With": "XMLHttpRequest",
        },
      });

      if (response.ok) {
        Turbo.renderStreamMessage(await response.text());
      } else {
        tbody.dataset.loaded = "true";
      }
    } catch (error) {
      tbody.dataset.loaded = "true";
    }
  }

  #rotateChevron(row, open) {
    const chevron = row.querySelector("i, svg");
    if (chevron) {
      chevron.style.transform = open ? "rotate(90deg)" : "";
    }
  }

  #hideDescendants(groupId) {
    const children = this.element.querySelectorAll(
      `[data-tree-table-parent-param="${groupId}"]`,
    );
    children.forEach((child) => {
      child.classList.add("hidden");
      if (child.hasAttribute("data-expanded")) {
        child.removeAttribute("data-expanded");
        this.#rotateChevron(child, false);
        const childGroupId = child.dataset.treeTableGroupIdParam;
        if (childGroupId) {
          this.#hideDescendants(childGroupId);
        }
      }
    });
  }

  #showDirectChildren(groupId) {
    const children = this.element.querySelectorAll(
      `[data-tree-table-parent-param="${groupId}"]`,
    );
    children.forEach((child) => {
      child.classList.remove("hidden");
    });
  }

  // --- State persistence ---

  #saveExpandedState() {
    if (!this.persistKeyValue) return;

    const expandedIds = [];
    this.element
      .querySelectorAll("[data-tree-table-group-id-param][data-expanded]")
      .forEach((row) => {
        expandedIds.push(row.dataset.treeTableGroupIdParam);
      });
    this.element
      .querySelectorAll("[data-tree-table-detail-id-param][data-expanded]")
      .forEach((row) => {
        expandedIds.push(`lazy:${row.dataset.treeTableDetailIdParam}`);
      });

    sessionStorage.setItem(this.persistKeyValue, JSON.stringify(expandedIds));
  }

  #restoreExpandedState() {
    const stored = sessionStorage.getItem(this.persistKeyValue);
    if (!stored) return;

    let expandedIds;
    try {
      expandedIds = JSON.parse(stored);
    } catch {
      return;
    }
    if (!Array.isArray(expandedIds) || expandedIds.length === 0) return;

    // Expand each saved group in order (parents before children)
    expandedIds.forEach((groupId) => {
      if (typeof groupId === "string" && groupId.startsWith("lazy:")) {
        const detailId = groupId.slice(5);
        const row = this.element.querySelector(
          `[data-tree-table-detail-id-param="${CSS.escape(detailId)}"]`,
        );
        if (!row || row.hasAttribute("data-expanded")) return;

        row.setAttribute("data-expanded", "");
        this.#rotateChevron(row, true);
        const detailTbody = document.getElementById(detailId);
        if (detailTbody) {
          detailTbody.classList.remove("hidden");
          const src = row.dataset.treeTableSrcParam;
          if (!detailTbody.dataset.loaded && src) {
            this.#fetchTurboStreamDetail(detailTbody, src);
          }
        }
        return;
      }

      const row = this.element.querySelector(
        `[data-tree-table-group-id-param="${CSS.escape(groupId)}"]`,
      );
      if (!row || row.hasAttribute("data-expanded")) return;

      row.setAttribute("data-expanded", "");
      row.classList.remove("hidden");
      this.#rotateChevron(row, true);
      this.#showDirectChildren(groupId);
    });
  }
}
